import { useMemo } from 'react';
import { formatCurrency, formatPercentage } from '../../Widgets/utils/formatNumber';
import { usePerformanceQuery } from '../api/usePerformanceQuery';
import { usePeriod } from './usePeriod';
import { useRouteParams } from './useRouteParams';

interface UsePerformanceSummaryParams {
  isTransactions?: boolean;
}

export const usePerformanceSummary = ({ isTransactions }: UsePerformanceSummaryParams) => {
  const period = usePeriod();

  const { accountId, clientId, groupId } = useRouteParams();

  const queryInfo = usePerformanceQuery({
    clientId,
    period: period.value,
    startDate: period.dates[0],
    endDate: period.dates[1],
    accountId,
    groupId,
  });

  const data = useMemo(() => {
    if (!queryInfo.data) {
      return null;
    }

    const { performanceSummary, transactionsSummary } = queryInfo.data;

    const summaryItems = isTransactions
      ? transactionsSummary.items.filter(
          ({ value, isFromOrionSetting }) => !isFromOrionSetting || value !== 0,
        )
      : performanceSummary.items;

    return summaryItems.map(({ isHighlighted, type, name, value }) => ({
      label: name,
      value:
        type === 'Currency'
          ? formatCurrency(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : formatPercentage(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      type: type.toLowerCase(),
      isHighlighted,
    }));
  }, [isTransactions, queryInfo.data]);

  return { ...queryInfo, data };
};
